import { useToken } from "@chakra-ui/react";
import {
  bbox as getBounds,
  buffer as getBuffer,
  distance,
  nearestPointOnLine,
  point,
  pointToLineDistance,
  type Feature,
  type Geometry,
  polygonToLineString,
} from "@turf/turf";
import { getInitialViewState, type LatLonType } from "@ui/maps/common";
import {
  subscriberLayerMap,
  type SubscriberSubcategoryLayerDefinition,
} from "@ui/maps/interactive-map/src/helpers";
import { type SubscriberMapPopupProps } from "@ui/maps/interactive-map/src/subscriber-map-popup";
import { useCallback, useRef, useState } from "react";
import {
  type LayerProps,
  type LngLatBoundsLike,
  type MapLayerMouseEvent,
  type MapRef,
} from "react-map-gl";
import { getHazardMapLegend } from "../helpers";
import { distanceHelper } from "@areahub/common";

export interface UseProReportHazardMapOptions {
  userLocation: LatLonType;
  features?: Feature<Geometry, any>[];

  distance?: number;
  category: string;

  displayName: string;

  layerProps?: LayerProps;

  color?: string;

  singularName?: string;

  zoom?: number;

  customHoverProps?: SubscriberSubcategoryLayerDefinition["hoverProps"];
}

export function useProReportHazardMap(options: UseProReportHazardMapOptions) {
  const { userLocation } = options;
  const [hovered, setHovered] = useState<SubscriberMapPopupProps | null>(null);
  const [clicked, setClicked] = useState<SubscriberMapPopupProps | null>(null);
  const initialViewState = getInitialViewState({
    latitude: userLocation.lat,
    longitude: userLocation.lon,
    zoom: options?.zoom || 7,
  });
  const [color] = useToken("colors", [options?.color || "blue.800"]);
  const buffer = getBuffer(
    point([userLocation.lon, userLocation.lat]),
    options?.distance || 5,
    {
      units: "miles",
    },
  );
  const bbox = getBounds(buffer);

  const ref = useRef<MapRef>();
  const onLoad = useCallback(() => {
    resetViewState();
  }, []);
  const resetViewState = () =>
    options?.zoom
      ? ref?.current?.easeTo({
          center: [userLocation.lon, userLocation.lat],
          zoom: options.zoom,
          duration: 800,
        })
      : ref?.current?.fitBounds(bbox as LngLatBoundsLike, {
          padding: 25,
          duration: 800,
        });

  const legendRows = getHazardMapLegend(
    options.displayName,
    options?.features?.[0],
    color,
  );

  const getInteractiveState = (
    feature: mapboxgl.MapboxGeoJSONFeature | undefined,
    event: MapLayerMouseEvent,
  ) => {
    if (feature?.geometry && feature?.id) {
      const qLoc = point([userLocation.lon, userLocation.lat]);
      const distanceFromUserLocation = distanceHelper(
        qLoc.geometry.coordinates,
        feature?.geometry as Geometry,
        "miles",
      );
      const def = subscriberLayerMap.get(feature?.source as string);
      const hoverProps = options?.customHoverProps ?? def?.hoverProps;

      return {
        id: feature?.id as string,
        lat: event.lngLat.lat,
        lon: event.lngLat.lng,
        source: feature?.source as string,
        singularName: def?.singularName as string,
        title: hoverProps?.title(feature?.properties),
        subtitle: hoverProps?.subtitle(feature?.properties),
        children: hoverProps?.children
          ? hoverProps?.children(feature?.properties)
          : "",
        distance: distanceFromUserLocation
          ? `${distanceFromUserLocation.toFixed(2)} Miles Away`
          : "",
      } satisfies SubscriberMapPopupProps;
    }
    return null;
  };

  const onMouseMove = useCallback(
    (event: MapLayerMouseEvent) => {
      const feature = event?.features?.[0];
      if (hovered?.id) {
        ref?.current?.setFeatureState(
          { source: options.category, id: hovered?.id },
          { hover: false },
        );
        setHovered(null);
      }

      if (feature?.geometry && feature?.id) {
        const interactiveState = getInteractiveState(feature, event);

        setHovered(interactiveState);
        ref?.current?.setFeatureState(
          {
            source: options.category,
            id: feature?.id,
          },
          { hover: true },
        );
      }
    },
    [hovered],
  );
  const onClick = useCallback(
    (event: MapLayerMouseEvent) => {
      const feature = event?.features?.[0];
      if (clicked?.id) {
        ref?.current?.setFeatureState(
          { source: options.category, id: clicked?.id },
          { clicked: false },
        );
        setClicked(null);
      }
      if (feature?.geometry && feature?.id) {
        const interactiveState = getInteractiveState(feature, event);
        setClicked(interactiveState);
        ref?.current?.setFeatureState(
          {
            source: options.category,
            id: feature?.id,
          },
          { clicked: true },
        );
      }
    },
    [clicked],
  );
  return {
    ref,
    resetViewState,
    buffer,
    hovered,
    clicked,
    initialViewState,
    onLoad,
    onMouseMove,
    onClick,
    legendRows,
    color,
  };
}
